import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useReadContract,
  useSwitchChain,
  useAccount as useWagmiAccount,
  useWriteContract,
} from 'wagmi';
import STS_ABI from '@/lib/constants/sts-abi';
import { currentChain } from '@/lib/wagmi';
import { useTronAddress } from './use-tron';

export function useEvmPause() {
  const { t } = useTranslation();
  const { address, chain } = useWagmiAccount();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();

  const isPausedQuery = useReadContract({
    address: '0x4493c1f8FCa65Ca53E0D37981e479087f4E7C250',
    abi: STS_ABI,
    functionName: 'paused',
    query: {
      enabled: !!address,
    },
  });

  return {
    pause: useMutation({
      mutationFn: async () => {
        if (!address) throw new Error(t('errors.walletNotConnected'));
        if (chain?.id !== currentChain.id) {
          await switchChainAsync({ chainId: currentChain.id });
        }
        await writeContractAsync({
          address: '0x4493c1f8FCa65Ca53E0D37981e479087f4E7C250',
          abi: STS_ABI,
          functionName: 'pause',
          args: [],
        });
      },
    }),
    isPausedQuery,
  };
}

export function useTronPause() {
  const { t } = useTranslation();
  const tronAddress = useTronAddress();

  const contract = useMemo(
    () =>
      window.tronWeb?.contract(STS_ABI, 'TFXGfwu7YvNMkQRseEWt4KBpLMit7T6jMX'),
    [],
  );

  return {
    pause: useMutation({
      mutationFn: async () => {
        if (!contract) throw new Error(t('errors.contractNotFound'));
        if (!tronAddress) throw new Error(t('errors.walletNotConnected'));
        await contract.pause().send({ callValue: 0, feeLimit: 100_000_000 });
      },
    }),
    isPausedQuery: useQuery({
      queryKey: ['isPaused', 'tron'],
      queryFn: async () => {
        if (!contract) throw new Error(t('errors.contractNotFound'));
        const result = await contract.paused().call();
        return result;
      },
    }),
  };
}
